import i18next from "i18next";
import { Button, Col, Container, Modal, Row, Spinner } from "react-bootstrap";
import { useAccountExternalApi } from "src/hooks/useAccountExternalApi";
import { AccountCircolante } from "./AccountCircolante";
import { AccountOrdinato } from "./AccountOrdinato";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaArrowRotateRight } from "react-icons/fa6";
import ToolTip from 'src/components/Tooltip/Tooltip';
// @ts-ignore
import './AccountExternalData.css'
import _ from 'lodash'
import { FileExportMenu } from "../FileExportMenu/FileExportMenu";
import { useErrorToast } from "src/hooks/useErrorToast";
import { GTMsendEvent } from "src/utils/trackingGA4";
import { downloadCSV, downloadXLSX } from "src/utils";
import { CIRCOLANTE_COLUMNS, ORDINATO_COLUMNS } from "./externalDataTableColumns";
import { useAuth } from "src/hooks/useAuth";
import { RoleEnum } from "src/api";

type Props = {
  accountId: string;
}

function ReloadAction({ isLoading, actionName, action, tooltip }) {
  return (
    <Button
      id={`reload-action-${actionName}`}
      disabled={isLoading}
      variant="link"
      onClick={() => action?.()}
    >
      {isLoading ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <FaArrowRotateRight />
      )}
      <ToolTip
        anchorId={`reload-action-${actionName}`}
        content={tooltip}
        className="default"
      />
    </Button>
  )
}

const labelTransformer = (key: string) => {
  return {
    id: i18next.t('id'),
    rental: i18next.t('rental'),
    account_id: i18next.t('account_id'),
    contratto_id: i18next.t('contratto_id'),
    targa: i18next.t('targa'),
    telaio: i18next.t('telaio'),
    data_immatricolazione: i18next.t('data_immatricolazione'),
    marca: i18next.t('marca'),
    modello: i18next.t('modello'),
    allestimento: i18next.t('allestimento'),
    durata_contratto: i18next.t('durata_contratto'),
    km_contratto: i18next.t('km_contratto'),
    inizio_contratto: i18next.t('inizio_contratto'),
    fine_contratto: i18next.t('fine_contratto'),
    canone_totale: i18next.t('canone_totale'),
    km_ultimo_rilevamento: i18next.t('km_ultimo_rilevamento'),
    data_ultimo_rilevamento: i18next.t('data_ultimo_rilevamento'),
    cliente: i18next.t('cliente'),
    partita_iva: i18next.t('partita_iva'),
    cf: i18next.t('cf'),
    stato: i18next.t('stato'),
    data_consegna: i18next.t('data_prevista_consegna'),
    luogo_consegna: i18next.t('luogo_consegna'),
    sf_titolare: i18next.t('sf_titolare'),
    sf_filiale: i18next.t('sf_filiale'),
    sf_nominativo: i18next.t('sf_nominativo'),
    sf_piva: i18next.t('sf_piva'),
    sf_cf: i18next.t('sf_cf'),
  }[key] || (
      key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (match) => match.toUpperCase())
    )
}

export function AccountExternalData({ accountId }: Props) {
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const { userRole } = useAuth();
  const { loadingState, circolante, ordinato, retrieveExternalData, actions, } = useAccountExternalApi(accountId)
  const { showError } = useErrorToast();

  useEffect(() => {
    retrieveExternalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = useCallback(async (format, type: string) => {
    let dataToExport = type === 'ordinato'
      ? (ordinato.data || []).slice()
      : (circolante.data || []).slice();

    if (!dataToExport.length) {
      showError(null, i18next.t('no_data_available_for_download'));
      return;
    }

    const columns = type === 'ordinato'
      ? ORDINATO_COLUMNS()
      : CIRCOLANTE_COLUMNS();

    const exportableColumns = (columns || []).filter(
      (col) => !['checkboxes', 'id', 'actions'].includes(col.id)
    );

    const csvData = [];

    // @ts-ignore
    csvData.push(exportableColumns.map((col) => col.Header));

    dataToExport.forEach((rowOriginal) => {
      const rowValues = exportableColumns.map((col) => {
        let value = rowOriginal[col.accessor];

        if (typeof value === 'string') {
          value = value.replace(/\n/g, ' ');
        }

        return value || '';
      });
      // @ts-ignore
      csvData.push(rowValues);
    });

    const name = type === 'ordinato'
      ? i18next.t('account_ordinato_section')
      : i18next.t('account_circolante_section');

    let fileName = [name].join('_').replace(' ', '_');

    switch (format) {
      case 'xlsx':
        downloadXLSX(csvData, fileName);
        break;
      case 'csv':
        downloadCSV(csvData, fileName);
        break;
      default:
        console.warn(`[Horizon] Unsupported export format: ${format}`);
        break;
    }

    GTMsendEvent({
      event: "CTA_download_file",
      evtCat: type === 'ordinato' ? 'account_ordinato' : 'account_circolante',
      evtAct: "download",
      evtLab: `${fileName}.${format}`
    });

  }, [showError]);


  const selectItem = (item: any) => {
    const omittedField = {
      ordinato: ['id', 'inizio_contratto', 'fine_contratto', 'km_ultimo_rilevamento', 'data_ultimo_rilevamento'],
      circolante: ['id', 'data_consegna'],
    }
    setSelectedItem(Object.defineProperty(
      _.omit(item, omittedField[item.$type] || []), '$type', {
      value: item.$type,
      enumerable: false,
    }));
  }

  const itemType = useMemo(() => {
    if (!selectedItem) return '';
    if (!selectedItem.$type) return '';

    const label = {
      circolante: i18next.t('consegna'),
      ordinato: i18next.t('ordine'),
    }[selectedItem?.$type]

    return label ? ` - ${label}` : '';
  }, [selectedItem])

  const canDownload = useMemo(() => {
    const allowedRoles = [RoleEnum.ADMIN, RoleEnum.AREA_MANAGER, RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL];
    return allowedRoles.includes(userRole);
  }, [userRole])

  return (
    <div style={{ marginTop: '76px' }}>
      <hr />
      <Row>
        <Col className="display-7">
          {i18next.t('account_ordinato_section')}  <span className="lead">{`(${ordinato.data?.length || 0})`}</span>
          <ReloadAction
            actionName="ordinato"
            isLoading={loadingState.isLoadingOrdinato}
            action={() => actions.refreshOrdinato()}
            tooltip={i18next.t('reload_ordinato')}
          />
        </Col>
        {canDownload && (
          <Col md={6} className="mt-4 mt-md-0 text-md-end p-0">
            <FileExportMenu
              isDisabled={loadingState.isLoadingOrdinato || !ordinato?.data?.length}
              allowedFormats={['csv', 'xlsx']}
              handleExport={(format) => handleExport(format, 'ordinato')}
            />
          </Col>
        )}
      </Row>
      <AccountOrdinato
        accountId={accountId}
        loadingState={loadingState}
        ordinato={ordinato}
        onSelectItem={selectItem}
      />
      <hr />
      <Row>
        <Col className="display-7">
          {i18next.t('account_circolante_section')}  <span className="lead">{`(${circolante.data?.length || 0})`}</span>
          <ReloadAction
            actionName="circolante"
            isLoading={loadingState.isLoadingCircolante}
            action={() => actions.refreshCircolante()}
            tooltip={i18next.t('reload_circolante')}
          />
        </Col>
        {canDownload && (
          <Col md={6} className="mt-4 mt-md-0 text-md-end p-0">
            <FileExportMenu
              isDisabled={loadingState.isLoadingCircolante || !circolante.data?.length}
              allowedFormats={['csv', 'xlsx']}
              handleExport={(format) => handleExport(format, 'circolante')}
            />
          </Col>)
        }
      </Row>
      <AccountCircolante
        accountId={accountId}
        loadingState={loadingState}
        circolante={circolante}
        onSelectItem={selectItem}
      />
      <Modal
        show={!!selectedItem}
        size="xl"
        onExit={() => setSelectedItem(null)}
      >
        <Modal.Header>
          <Modal.Title>{i18next.t("details")} {itemType}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PropertyGrid
            data={selectedItem || {}}
            labelTransform={labelTransformer}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setSelectedItem(null)}>
            {i18next.t('cancel')}
          </Button>
        </Modal.Footer>
      </Modal>

    </div >
  )
}

interface PropertyGridProps {
  data: { [key: string]: any };
  labelTransform?: (key: string) => string;
}

const PropertyGrid: React.FC<PropertyGridProps> = ({ data, labelTransform }) => {
  const transformLabel = (key: string) => {
    if (labelTransform) return labelTransform(key);
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (match) => match.toUpperCase());
  };

  return (
    <Container className="property-grid">
      <Row>
        {Object.entries(data).map(([key, value]) => (
          <Col xs={12} sm={6} md={4} lg={3} key={key} className="property-item">
            <div>
              <label className="property-label">{transformLabel(key)}</label>
              <div className="property-value">{value ?? 'N/A'}</div>
            </div>
          </Col>
        ))}
      </Row>
    </Container>
  );
};
